/**
 * Authentication Hook
 *
 * Provides authentication functionality including wallet signature flow.
 * Integrates with the backend JWT auth system.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import bs58 from 'bs58';
import { useWalletInfo } from '@reown/appkit/react';

import { useAuthContext } from '@/context/AuthContext';
import { usePhantom } from '@/context/PhantomContext';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import * as authApi from '@/api/auth';
import { logger } from '@/lib/logger';

/**
 * Hook for authentication operations
 */
export function useAuth() {
  const { address, isConnected, walletProvider, isPhantomActive, isGateDirectActive } = useWalletAddress();
  const { walletInfo } = useWalletInfo('solana');
  const { signPhantomMessage } = usePhantom();
  const { isAuthenticated, userInfo, signInWithWallet, signOut, sessionExpired, setSessionExpired, isVerifyingSession } = useAuthContext();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  /**
   * Authenticate with wallet signature
   */
  const authenticate = useCallback(async () => {
    if (!address || !isConnected) {
      toast.error('Please connect your wallet first');
      return false;
    }

    // Check if we have a provider
    if (!walletProvider) {
      toast.error('Wallet provider not found');
      return false;
    }

    setIsAuthenticating(true);

    try {
      // Detect wallet name based on active provider
      let detectedWalletName: string | undefined;

      if (isPhantomActive) {
        // Using Phantom SDK directly
        detectedWalletName = 'Phantom';
        logger.log('[useAuth] 🎯 Using Phantom SDK');
      } else if (isGateDirectActive) {
        // Using Gate Wallet adapter directly
        detectedWalletName = 'Gate Wallet';
        logger.log('[useAuth] 🎯 Using Gate Wallet direct adapter');
      } else {
        // Check walletProvider properties (this is the actual connected wallet)
        const providerAny = walletProvider as any;

        logger.log('[useAuth] 🔍 walletProvider object:', walletProvider);
        logger.log('[useAuth] 🔍 walletProvider.name:', providerAny?.name);
        logger.log('[useAuth] 🔍 walletProvider._wallet:', providerAny?._wallet);
        logger.log('[useAuth] 🔍 walletInfo from AppKit:', walletInfo);

        // PRIORITY 1: Use walletInfo from AppKit (most reliable for WalletConnect wallets)
        if (walletInfo?.name) {
          // Normalize wallet names for known wallets
          const walletName = walletInfo.name;
          const walletNameLower = walletName.toLowerCase();
          const rdnsLower = (walletInfo.rdns || '').toLowerCase();

          // Gate Wallet detection
          if (walletNameLower.includes('gate') || rdnsLower.includes('gate')) {
            detectedWalletName = 'Gate Wallet';
            logger.log('[useAuth] 🎯 Detected Gate Wallet from walletInfo');
          }
          // Solflare detection
          else if (walletNameLower.includes('solflare') || rdnsLower.includes('solflare')) {
            detectedWalletName = 'Solflare';
            logger.log('[useAuth] 🎯 Detected Solflare from walletInfo');
          }
          // Backpack detection
          else if (walletNameLower.includes('backpack') || rdnsLower.includes('backpack')) {
            detectedWalletName = 'Backpack';
            logger.log('[useAuth] 🎯 Detected Backpack from walletInfo');
          }
          // Phantom detection (via WalletConnect, not embedded SDK)
          else if (walletNameLower.includes('phantom') || rdnsLower.includes('phantom')) {
            detectedWalletName = 'Phantom';
            logger.log('[useAuth] 🎯 Detected Phantom from walletInfo');
          }
          // Use the name as-is for other wallets
          else {
            detectedWalletName = walletName;
            logger.log('[useAuth] 🎯 Using walletInfo.name directly:', detectedWalletName);
          }
        }
        // PRIORITY 2: Try provider properties
        else if (providerAny?.name) {
          detectedWalletName = providerAny.name;
          logger.log('[useAuth] 🎯 Detected from provider.name:', detectedWalletName);
        } else if (providerAny?._wallet?.adapter?.name) {
          detectedWalletName = providerAny._wallet.adapter.name;
          logger.log('[useAuth] 🎯 Detected from _wallet.adapter.name:', detectedWalletName);
        } else if (providerAny?.adapter?.name) {
          detectedWalletName = providerAny.adapter.name;
          logger.log('[useAuth] 🎯 Detected from adapter.name:', detectedWalletName);
        }

        // PRIORITY 3: Fallback to window object checks (least reliable)
        if (!detectedWalletName && typeof window !== 'undefined') {
          const solanaWallet = (window as any).solana;
          if (solanaWallet) {
            // Check for Gate Wallet first (before Phantom, as Gate may expose isPhantom for compatibility)
            if ((window as any).gatewallet || solanaWallet.isGateWallet) {
              detectedWalletName = 'Gate Wallet';
              logger.log('[useAuth] 🎯 Fallback detected Gate Wallet from window');
            } else if (solanaWallet.isBackpack) {
              detectedWalletName = 'Backpack';
              logger.log('[useAuth] 🎯 Fallback detected from window.solana.isBackpack');
            } else if ((window as any).solflare?.isSolflare) {
              detectedWalletName = 'Solflare';
              logger.log('[useAuth] 🎯 Fallback detected from window.solflare.isSolflare');
            } else if (solanaWallet.isPhantom) {
              detectedWalletName = 'Phantom';
              logger.log('[useAuth] 🎯 Fallback detected from window.solana.isPhantom');
            }
          }
        }
      }

      logger.log('[useAuth] ✅ Final detected wallet:', detectedWalletName);

      // Step 1: Get message to sign from backend
      const messageResponse = await authApi.getAuthMessage(address!);

      if (messageResponse.code !== 0) {
        throw new Error(messageResponse.message || 'Failed to get auth message');
      }

      const messageToSign = messageResponse.data.message;

      // Step 2: Sign message with appropriate provider
      const encodedMessage = new TextEncoder().encode(messageToSign);
      let signature: string;

      // Check if we're using the Phantom provider (unified hook provides a wrapped provider)
      const providerAny = walletProvider as any;
      logger.log('[useAuth] 🔍 Provider check:', {
        isPhantom: providerAny?.isPhantom,
        isGateWallet: providerAny?.isGateWallet,
        hasSignMessage: typeof providerAny?.signMessage === 'function',
        isGateDirectActive,
      });

      if (providerAny?.isPhantom) {
        // Use Phantom SDK for signing (provider is wrapped by unified hook)
        const signedMessage = await signPhantomMessage(encodedMessage);
        signature = bs58.encode(signedMessage);
        logger.log('[useAuth] 🔐 Message signed with Phantom SDK');
      } else if (providerAny?.isGateWallet || isGateDirectActive) {
        // Use Gate Wallet adapter for signing
        logger.log('[useAuth] 🔐 Attempting Gate Wallet signing...');
        if (typeof providerAny?.signMessage !== 'function') {
          // Try getting the adapter directly as fallback
          const { getGateWalletAdapter } = await import('@/adapters/GateWalletAdapter');
          const adapter = getGateWalletAdapter();
          logger.log('[useAuth] 🔐 Using Gate adapter directly:', { connected: adapter.connected, hasSignMessage: typeof adapter.signMessage === 'function' });
          const signedMessage = await adapter.signMessage(encodedMessage);
          signature = bs58.encode(signedMessage);
        } else {
          const signedMessage = await providerAny.signMessage(encodedMessage);
          signature = bs58.encode(signedMessage);
        }
        logger.log('[useAuth] 🔐 Message signed with Gate Wallet');
      } else {
        // Use standard wallet provider for signing
        if (!providerAny.signMessage) {
          throw new Error('Wallet provider does not support message signing');
        }
        const signedMessage = await providerAny.signMessage(encodedMessage);
        signature = bs58.encode(signedMessage);
        logger.log('[useAuth] 🔐 Message signed with standard provider');
      }

      // Step 3: Submit signature to backend for JWT token
      await signInWithWallet(signature, messageToSign, detectedWalletName);

      return true;
    } catch (error) {
      logger.error('Authentication error:', error);
      toast.error(error instanceof Error ? error.message : 'Authentication failed');
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, [address, isConnected, walletProvider, walletInfo, signPhantomMessage, signInWithWallet, isPhantomActive, isGateDirectActive]);

  /**
   * Sign out
   */
  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      logger.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  // Auto-trigger authentication when session expires but wallet is still connected
  const hasTriggeredAuth = useRef(false);
  const prevConnected = useRef(isConnected);

  useEffect(() => {
    // Check if wallet just connected (was disconnected, now connected)
    const justConnected = !prevConnected.current && isConnected;
    prevConnected.current = isConnected;

    // Trigger auth if:
    // 1. Session expired and wallet is connected
    // 2. Wallet just connected and session is expired
    // 3. Wallet is connected but not authenticated (and not already trying)
    // Always skip if we're currently verifying an existing session
    const shouldAutoAuth = (
      !isVerifyingSession &&
      ((sessionExpired && isConnected && !isAuthenticated && !isAuthenticating) ||
       (justConnected && sessionExpired && !isAuthenticated && !isAuthenticating) ||
       (justConnected && !isAuthenticated && !isAuthenticating && userInfo))
    );

    if (shouldAutoAuth && !hasTriggeredAuth.current) {
      logger.log('[useAuth] Auto-triggering authentication', {
        sessionExpired,
        isConnected,
        isAuthenticated,
        isAuthenticating,
        justConnected,
        hasUserInfo: !!userInfo
      });
      hasTriggeredAuth.current = true;

      // Small delay to ensure wallet is fully ready
      setTimeout(() => {
        authenticate().then((success) => {
          if (success) {
            setSessionExpired(false);
            hasTriggeredAuth.current = false;
          }
          // On failure, do NOT reset hasTriggeredAuth - this prevents infinite auth loops
          // User must manually trigger auth or disconnect/reconnect wallet
        });
      }, 500);
    }

    // Reset flag when wallet disconnects (allows fresh auth on reconnect)
    if (!isConnected) {
      hasTriggeredAuth.current = false;
    }
    // Also reset when session is no longer expired and user didn't just connect
    else if (!sessionExpired && !justConnected) {
      hasTriggeredAuth.current = false;
    }
  }, [sessionExpired, isConnected, isAuthenticated, isAuthenticating, isVerifyingSession, authenticate, setSessionExpired, userInfo]);

  return {
    // State
    isAuthenticated,
    isAuthenticating,
    isVerifyingSession,
    userInfo,
    address,
    isConnected,

    // Methods
    authenticate,
    logout,
  };
}
